import type { DailyRevenue } from './types';

/**
 * Ventas del día a día (rediseño, decidido con Alex el 11 oct 2026).
 *
 * Las reglas que comparten la pantalla de Ventas, el panel del día, el Panel
 * («¿Cuadran tus ventas?») y los KPI:
 *
 * - Todo se guarda SIN IVA. Se anota como sale en el corte de caja (con IVA,
 *   lo normal) y se divide entre (1 + IVA) al guardar.
 * - Vendiste = caja (efectivo + tarjeta + otros) + apps. «Para llevar» ya está
 *   dentro de la caja: solo se separa, nunca se suma otra vez.
 * - Los tickets solo con datos: un día con ventas pero sin clientes no entra
 *   en «Cada cliente» (el ticket saldría inflado), y sin pedidos no hay «Por
 *   pedido». Nunca se divide entre 0.
 * - Un día sin datos no es «0»: es «Sin anotar», «Cerrado» u «Hoy».
 */

type Dia = Pick<DailyRevenue, 'inHouse' | 'delivery'> & Partial<Pick<DailyRevenue,
  'customers' | 'tables' | 'deliveryOrders' | 'takeaway' | 'takeawayOrders' | 'courtesies' | 'discounts' | 'closed'>>;

const n = (value: number | undefined | null): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const suma = (values: Record<string, number | undefined> | undefined | null): number =>
  Object.values(values ?? {}).reduce<number>((a, b) => a + n(b), 0);
const round2 = (value: number): number => Math.round(value * 100) / 100;

/** Lo que entró en la caja: efectivo + tarjeta + otros (incluye para llevar). */
export const cajaDe = (day: Dia): number => suma(day.inHouse);
/** Lo vendido en apps, antes de su comisión. */
export const appsDe = (day: Dia): number => suma(day.delivery);
/** Vendiste = caja + apps. */
export const vendisteDe = (day: Dia): number => cajaDe(day) + appsDe(day);
/** Para llevar, nunca más que lo que entró en la caja. */
export const paraLlevarDe = (day: Dia): number => Math.min(n(day.takeaway), cajaDe(day));
/** Comieron aquí = caja − para llevar. Base de «Cada cliente» y «Por mesa». */
export const comieronAquiDe = (day: Dia): number => Math.max(0, cajaDe(day) - paraLlevarDe(day));
/** Pedidos: para llevar + apps. */
export const pedidosDe = (day: Dia): number => n(day.takeawayOrders) + n(day.deliveryOrders);
/** Lo vendido en pedidos: para llevar + apps. */
export const ventaPedidosDe = (day: Dia): number => paraLlevarDe(day) + appsDe(day);
/**
 * Lo vendido en pedidos que tienen su número de pedidos ese día: para llevar
 * solo si hay pedidos para llevar, apps solo si hay pedidos de apps. Es el
 * numerador de «Por pedido» (si no, 500 de Uber sin pedidos inflaban el ticket).
 */
export const ventaConPedidosDe = (day: Dia): number =>
  (n(day.takeawayOrders) > 0 ? paraLlevarDe(day) : 0) + (n(day.deliveryOrders) > 0 ? appsDe(day) : 0);

/** Importe del corte (con IVA) → lo que se guarda (sin IVA). */
export function sinIva(amount: number, vatPct: number): number {
  return amount / (1 + n(vatPct) / 100);
}
/** Lo guardado (sin IVA) → como sale en el corte (con IVA), al céntimo. */
export function conIva(net: number, vatPct: number): number {
  return round2(net * (1 + n(vatPct) / 100));
}

export interface ResumenVentas {
  vendiste: number;
  caja: number;
  apps: number;
  paraLlevar: number;
  comieronAqui: number;
  clientes: number;
  mesas: number;
  pedidos: number;
  ventaPedidos: number;
  /** Ausente si ningún día tiene clientes. */
  cadaCliente?: number;
  porMesa?: number;
  porPedido?: number;
  cortesias: number;
  descuentos: number;
  /** Días con ventas. */
  diasAnotados: number;
}

export function resumenVentas(days: Dia[]): ResumenVentas {
  let conClientes = 0;
  let conMesas = 0;
  let conPedidos = 0;
  const r: ResumenVentas = {
    vendiste: 0, caja: 0, apps: 0, paraLlevar: 0, comieronAqui: 0,
    clientes: 0, mesas: 0, pedidos: 0, ventaPedidos: 0,
    cortesias: 0, descuentos: 0, diasAnotados: 0,
  };
  for (const day of days) {
    const vendiste = vendisteDe(day);
    r.vendiste += vendiste;
    r.caja += cajaDe(day);
    r.apps += appsDe(day);
    r.paraLlevar += paraLlevarDe(day);
    r.comieronAqui += comieronAquiDe(day);
    r.cortesias += n(day.courtesies);
    r.descuentos += n(day.discounts);
    if (vendiste > 0) r.diasAnotados += 1;
    // Solo los días que tienen el dato entran en su ticket.
    if (n(day.customers) > 0) { r.clientes += n(day.customers); conClientes += comieronAquiDe(day); }
    if (n(day.tables) > 0) { r.mesas += n(day.tables); conMesas += comieronAquiDe(day); }
    if (pedidosDe(day) > 0) { r.pedidos += pedidosDe(day); conPedidos += ventaConPedidosDe(day); }
    r.ventaPedidos += ventaPedidosDe(day);
  }
  if (r.clientes > 0) r.cadaCliente = round2(conClientes / r.clientes);
  if (r.mesas > 0) r.porMesa = round2(conMesas / r.mesas);
  if (r.pedidos > 0) r.porPedido = round2(conPedidos / r.pedidos);
  return r;
}

export type EstadoDia = 'anotado' | 'sin_anotar' | 'cerrado' | 'hoy' | 'futuro';

/** 0 = domingo … 6 = sábado, como `Date.getUTCDay`. */
export const diaDeLaSemana = (date: string): number => new Date(`${date}T00:00:00Z`).getUTCDay();

/**
 * Estado de un día. Anotado gana siempre (si vendió, no estaba cerrado);
 * luego un cierre a mano o un día fijo de cierre; hoy no es una falta.
 */
export function estadoDia(date: string, day: Dia | undefined, closedWeekdays: number[] | undefined, hoy: string): EstadoDia {
  if (day && vendisteDe(day) > 0) return 'anotado';
  if (day?.closed) return 'cerrado';
  if (closedWeekdays?.includes(diaDeLaSemana(date))) return 'cerrado';
  if (date > hoy) return 'futuro';
  if (date === hoy) return 'hoy';
  return 'sin_anotar';
}

/** Todas las fechas YYYY-MM-DD de un mes. */
export function diasDelMes(month: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: ultimo }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/** Junta los días de varios locales por fecha (el consolidado). */
export function porFecha<T extends Dia & { date: string }>(days: T[]): Map<string, Dia> {
  const out = new Map<string, Dia>();
  for (const day of days) {
    const prev = out.get(day.date);
    if (!prev) { out.set(day.date, day); continue; }
    const juntar = (a: Record<string, number> | undefined, b: Record<string, number> | undefined) => {
      const r: Record<string, number> = { ...(a ?? {}) };
      for (const [k, v] of Object.entries(b ?? {})) r[k] = n(r[k]) + n(v);
      return r;
    };
    out.set(day.date, {
      inHouse: juntar(prev.inHouse, day.inHouse) as Dia['inHouse'],
      delivery: juntar(prev.delivery, day.delivery),
      customers: n(prev.customers) + n(day.customers) || undefined,
      tables: n(prev.tables) + n(day.tables) || undefined,
      deliveryOrders: n(prev.deliveryOrders) + n(day.deliveryOrders) || undefined,
      takeaway: n(prev.takeaway) + n(day.takeaway) || undefined,
      takeawayOrders: n(prev.takeawayOrders) + n(day.takeawayOrders) || undefined,
      courtesies: n(prev.courtesies) + n(day.courtesies) || undefined,
      discounts: n(prev.discounts) + n(day.discounts) || undefined,
      closed: Boolean(prev.closed && day.closed),
    });
  }
  return out;
}

/** Días pasados del mes sin anotar ni cerrados. Hoy no cuenta. */
export function diasSinAnotar(
  month: string,
  days: Array<Dia & { date: string }>,
  closedWeekdays: number[] | undefined,
  hoy: string,
): string[] {
  const mapa = porFecha(days);
  return diasDelMes(month).filter((date) => estadoDia(date, mapa.get(date), closedWeekdays, hoy) === 'sin_anotar');
}

/**
 * Para comparar con el mes anterior con los MISMOS días: del 1 al 10 de
 * octubre contra del 1 al 10 de septiembre. Si el mes anterior es más corto,
 * se queda en su último día.
 */
export function mismosDiasDelMesAnterior(month: string, hastaDia: number): { month: string; start: string; end: string } {
  const [y, m] = month.split('-').map(Number);
  const prev = new Date(Date.UTC(y, m - 2, 1));
  const prevMonth = `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}`;
  const ultimo = new Date(Date.UTC(prev.getUTCFullYear(), prev.getUTCMonth() + 1, 0)).getUTCDate();
  const dia = Math.max(1, Math.min(hastaDia, ultimo));
  return { month: prevMonth, start: `${prevMonth}-01`, end: `${prevMonth}-${String(dia).padStart(2, '0')}` };
}

/* ------------------------------------------------------------------ */
/* «¿Cuadran tus ventas?» (tarea del Panel, del 1 al 10 de cada mes)    */
/* ------------------------------------------------------------------ */

/** Del 1 al 10 se pregunta por el mes anterior; el 11 desaparece sola. */
export function mesDelCuadre(hoy: string): string | undefined {
  if (Number(hoy.slice(8, 10)) > 10) return undefined;
  const [y, m] = hoy.slice(0, 7).split('-').map(Number);
  const prev = new Date(Date.UTC(y, m - 2, 1));
  return `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}`;
}

export interface DiaRaro { date: string; vendiste: number; normal: number }

export interface ComparacionCuadre {
  estado: 'cuadra' | 'falta' | 'sobra';
  /** Lo anotado en Alexia, con IVA, como lo da el punto de venta. */
  alexiaConIva: number;
  alexiaSinIva: number;
  /** Alexia − punto de venta, con IVA. Negativo = a Alexia le falta. */
  diferencia: number;
  diasSinAnotar: string[];
  /** Días muy por debajo (si falta) o por encima (si sobra) de lo normal para su día de la semana. */
  diasRaros: DiaRaro[];
}

/** Menos de 10 o del 0,1 %: redondeos, cuadra. */
export function cuadraConTolerancia(diferencia: number, total: number): boolean {
  return Math.abs(diferencia) < 10 || Math.abs(diferencia) <= Math.abs(total) * 0.001;
}

export function compararConPuntoDeVenta(
  days: Array<Dia & { date: string }>,
  month: string,
  vatPct: number,
  totalPuntoDeVentaConIva: number,
  closedWeekdays: number[] | undefined,
  hoy: string,
): ComparacionCuadre {
  const mapa = porFecha(days.filter((d) => d.date.startsWith(month)));
  const anotados = [...mapa.entries()].filter(([, d]) => vendisteDe(d) > 0);
  const alexiaSinIva = round2(anotados.reduce((a, [, d]) => a + vendisteDe(d), 0));
  const alexiaConIva = conIva(alexiaSinIva, vatPct);
  const diferencia = round2(alexiaConIva - totalPuntoDeVentaConIva);
  const estado = cuadraConTolerancia(diferencia, totalPuntoDeVentaConIva) ? 'cuadra' : diferencia < 0 ? 'falta' : 'sobra';

  // Lo normal de cada día de la semana: la media de ese día en el mes.
  const porSemana = new Map<number, number[]>();
  for (const [date, d] of anotados) {
    const w = diaDeLaSemana(date);
    porSemana.set(w, [...(porSemana.get(w) ?? []), vendisteDe(d)]);
  }
  const media = (w: number) => {
    const lista = porSemana.get(w) ?? [];
    return lista.length ? lista.reduce((a, b) => a + b, 0) / lista.length : 0;
  };
  const diasRaros = estado === 'cuadra' ? [] : anotados
    .map(([date, d]) => ({ date, vendiste: round2(vendisteDe(d)), normal: round2(media(diaDeLaSemana(date))) }))
    .filter((r) => (porSemana.get(diaDeLaSemana(r.date))?.length ?? 0) >= 2 && r.normal > 0)
    .filter((r) => (estado === 'falta' ? r.vendiste < r.normal * 0.6 : r.vendiste > r.normal * 1.6))
    .sort((a, b) => Math.abs(b.vendiste / b.normal - 1) - Math.abs(a.vendiste / a.normal - 1))
    .slice(0, 3)
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    estado,
    alexiaConIva,
    alexiaSinIva,
    diferencia,
    diasSinAnotar: diasSinAnotar(month, days.filter((d) => d.date.startsWith(month)), closedWeekdays, hoy),
    diasRaros,
  };
}

/**
 * Lo que Alexia entendió de un dictado o de la foto del corte, en las mismas
 * unidades en que se anota (con IVA si se anota con IVA). No guarda nada: el
 * panel del día lo pinta en verde para revisarlo.
 */
export interface RevenueFillFields {
  date?: string;
  efectivo?: number;
  tarjeta?: number;
  otros?: number;
  takeaway?: number;
  takeawayOrders?: number;
  /** Por platformId. */
  delivery?: Record<string, number>;
  platformOrders?: Record<string, number>;
  customers?: number;
  tables?: number;
  deliveryOrders?: number;
  courtesies?: number;
  discounts?: number;
  notes?: string;
}

export interface RevenueFill {
  fields: RevenueFillFields;
  /** Lo dicho que no encaja (una app que no tiene configurada…). */
  hints: string[];
}

/* ------------------------------------------------------------------ */
/* Punto de venta conectado que no trae ventas (revisión 14 oct 2026)  */
/* ------------------------------------------------------------------ */

/**
 * Último día con ventas que escribió un punto de venta (`source: pos:revo`…),
 * de entre los días que se tienen cargados.
 */
export function ultimaVentaDelPuntoDeVenta(
  days: Array<Dia & { date: string; source?: string }>,
  provider: string,
): string | undefined {
  return days
    .filter((d) => d.source === `pos:${provider}` && vendisteDe(d) > 0)
    .map((d) => d.date)
    .sort()
    .at(-1);
}

/**
 * Si está conectado, los días deberían estar. Cuando faltan días DESPUÉS de
 * la última venta que trajo, hay que decirlo: «Revo no manda ventas desde el
 * 30 sep · Ver». `null` = nada que avisar. Sin última venta conocida, el aviso
 * va sin fecha.
 */
export function puntoDeVentaSinVentas(args: { ultimaVenta?: string; sinAnotar: string[] }): { desde?: string } | null {
  const despues = args.sinAnotar.filter((d) => !args.ultimaVenta || d > args.ultimaVenta);
  if (despues.length === 0) return null;
  return args.ultimaVenta ? { desde: args.ultimaVenta } : {};
}
